import React from "react";
import { fmt } from "../../lib/sewingShapes";

/* ============================================================
   PatternPieces — the drafting-table half of the lab.

   Renders each of a model's flat pattern pieces as a small SVG
   with its cut edges colored by seam (matching the 3D view:
   same color = these edges sew together), plus the measurements
   and formulas you'd actually use to draw the piece on fabric.
   ============================================================ */

// One flat piece drawn true-to-shape inside a fitted viewBox.
const PieceSvg = ({ piece, tone }) => {
  const xs = piece.outline.map(([x]) => x);
  const ys = piece.outline.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const pad = Math.max(maxX - minX, maxY - minY) * 0.08 + 0.5;
  const vb = `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;
  const [hue, sat] = tone || [45, 80];

  return (
    <svg
      viewBox={vb}
      className="h-36 w-full"
      role="img"
      aria-label={`Flat pattern piece: ${piece.name}`}
    >
      <polygon
        points={piece.outline.map((p) => p.join(",")).join(" ")}
        fill={`hsl(${hue}, ${sat}%, 82%)`}
        fillOpacity="0.55"
        stroke="none"
      />
      {piece.folds2d.map(([a, b], i) => (
        <line
          key={`f${i}`}
          x1={a[0]}
          y1={a[1]}
          x2={b[0]}
          y2={b[1]}
          stroke="rgba(120,120,140,0.6)"
          strokeWidth="1.2"
          strokeDasharray="4 4"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {piece.edges.map((e, i) => (
        <line
          key={`e${i}`}
          x1={e.pts[0][0]}
          y1={e.pts[0][1]}
          x2={e.pts[1][0]}
          y2={e.pts[1][1]}
          stroke={e.color}
          strokeWidth="2.5"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {(piece.marks || []).map(([a, b], i) => (
        <line
          key={`m${i}`}
          x1={a[0]}
          y1={a[1]}
          x2={b[0]}
          y2={b[1]}
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          className="text-foreground/70"
        />
      ))}
    </svg>
  );
};

const PatternPieces = ({ model }) => {
  const seamById = Object.fromEntries(model.seams.map((s) => [s.id, s]));
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {model.pieces.map((piece) => {
        const colored = {
          ...piece,
          edges: piece.edges.map((e) => ({
            ...e,
            color: e.seam ? seamById[e.seam].color : "rgba(120,120,140,0.8)",
          })),
        };
        return (
          <div key={piece.id} className="rounded-xl border bg-card/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold capitalize">{piece.name}</h4>
              <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-primary">
                cut ×{piece.cut}
              </span>
            </div>
            <PieceSvg piece={colored} tone={model.pieceTones[piece.id]} />
            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {piece.notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

// Color key shared by the 3D view and the pattern pieces. Seams are listed
// in sewing order — real construction keeps the work flat as long as
// possible, and the gap seam is always last (it's closed by hand at the end).
export const SeamLegend = ({ model }) => (
  <ul className="space-y-1.5">
    {[...model.seams]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((seam) => (
        <li key={seam.id} className="flex items-start gap-2 text-xs text-muted-foreground">
          {seam.order && (
            <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-secondary text-[0.6rem] font-bold text-foreground">
              {seam.order}
            </span>
          )}
          <span
            aria-hidden
            className="mt-0.5 h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: seam.color }}
          />
          <span>
            {seam.label}
            {seam.gap && (
              <span className="ml-1.5 rounded-full border border-primary/40 bg-primary/10 px-1.5 py-px text-[0.6rem] font-medium uppercase tracking-wide text-primary">
                turning gap
              </span>
            )}
          </span>
        </li>
      ))}
    {model.folds.length > 0 && (
      <li className="flex items-start gap-2 text-xs text-muted-foreground">
        <span
          aria-hidden
          className="mt-1.5 h-0 w-3 shrink-0 border-t-2 border-dashed border-muted-foreground/70"
        />
        <span>fold lines — no sewing, the fabric just bends here</span>
      </li>
    )}
  </ul>
);

// A one-line "sanity ruler" so numbers keep meaning: roughly hand-sized etc.
export const scaleHint = (cm) => {
  if (cm < 6) return "about matchbox-sized";
  if (cm < 12) return "fits in your palm";
  if (cm < 20) return `about hand-sized (${fmt(cm)} cm)`;
  return "throw-pillow territory";
};

export default PatternPieces;
